// Global Instructions Rule Applied!
import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type CheckboxFieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { updateDealRecord } from '../../../../../state_manager/state_update_actions/deals/index.ts'
import { DealFields } from '../../../../../state_manager/data_models/deal_record.ts'
import { getRegisteredOfficeFields } from '../../../utils/fieldConfigs.ts'

interface RegisteredOfficeSectionProps {
  formData?: any
  handleFieldChange?: (key: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  isReadOnly?: boolean
  recordId?: string | number | null
  cardProps?: any
}

/**
 * Registered Office Section Component
 * Displays registered office address fields with inline editing capabilities
 * Used in DetailTab
 */
export function RegisteredOfficeSection({
  formData,
  handleFieldChange,
  onPatchSuccess,
  isReadOnly = false,
  recordId = null,
  cardProps = {}
}: RegisteredOfficeSectionProps) {
  const dispatch = useDispatch()

  const data = formData || {}

  const dealId = recordId || data[DealFields.deals__id] || data.id

  const accountId = data[DealFields.accounts__id] || data[DealFields.deals__account] || data.account_id

  const fieldOptions = {}

  const handlePatchSuccessLocal = (patchRecordId: string, updates: Record<string, any>) => {
    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(updateDealRecord({ dealId: dealId, updates }))
    }
  }

  const initialSameAsBusinessAddress =
    formData[DealFields.accounts__same_as_business_address] !== undefined
      ? formData[DealFields.accounts__same_as_business_address]
      : true

  const [sameAsBusinessAddress, setSameAsBusinessAddress] = useState<boolean>(initialSameAsBusinessAddress)

  useEffect(() => {
    const newValue =
      formData[DealFields.accounts__same_as_business_address] !== undefined
        ? formData[DealFields.accounts__same_as_business_address]
        : true
    setSameAsBusinessAddress(newValue)
  }, [formData[DealFields.accounts__same_as_business_address]])

  const handleCheckboxChange = (key: string, value: any) => {
    setSameAsBusinessAddress(value)
    handleFieldChange?.(key, value)
  }

  const checkboxField = useMemo<CheckboxFieldConfig>(
    () => ({
      key: DealFields.accounts__same_as_business_address,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value: sameAsBusinessAddress,
      checkboxLabel: 'Same as business address',
      editable: !isReadOnly
    }),
    [sameAsBusinessAddress, isReadOnly]
  )

  return (
    <FormSection
      data-debug='registered-office-section'
      data-component='RegisteredOfficeSection'
      title='Registered Office'
      titleColor='blue'
      {...cardProps}
    >
      <div data-debug='registered-office-section-content'>
        <div className='mb-3'>
          <InputManager
            inputMode='edit'
            labelPosition='inline'
            fields={[checkboxField]}
            onChange={handleCheckboxChange}
            enableAutoPatch={!isReadOnly && !!accountId}
            patchId={accountId}
            recordId={dealId}
            onPatchSuccess={handlePatchSuccessLocal}
            className='registered-office-checkbox'
          />
        </div>

        {!sameAsBusinessAddress && (
          <InputManager
            inputMode='read'
            labelPosition='inline'
            fields={getRegisteredOfficeFields(data, fieldOptions, isReadOnly)}
            onChange={(key: string, value: any) => handleFieldChange?.(key, value)}
            enableAutoPatch={!isReadOnly && !!accountId}
            patchId={accountId}
            recordId={dealId}
            onPatchSuccess={handlePatchSuccessLocal}
            className='registered-office-section-fields'
          />
        )}
      </div>
    </FormSection>
  )
}
